import { LLMMessage } from '@blisspointmedia/bpm-types/dist/LLM';
import { humanize } from 'inflected';
import remarkGfm from 'remark-gfm';
import type { Options } from 'react-markdown';
import {
  AgentResponse,
  AgentResponseSegment,
  AgentToolCallSegment
} from '../../utils/agent-client';
import { MAX_ATTACHMENT_BYTES } from './agent-chat-constants';
import { AgentMessage, ChatAttachment, MessageRole, PendingAttachment } from './agent-chat-types';

// Assistant messages are persisted as a JSON-serialized AgentResponseSegment[]
// (see the saveLLMMessage RESPONSE call in AgentSessionManager). Rows authored by
// other flows may instead hold plain response text, so fall back to a single text
// segment rather than dropping the turn.
const parseStoredSegments = (contents: string): AgentResponseSegment[] => {
  try {
    const parsed: unknown = JSON.parse(contents);
    if (Array.isArray(parsed)) return parsed as AgentResponseSegment[];
  } catch {
    // not JSON — fall through to the plain-text segment below
  }
  return [{ type: 'text', content: contents }];
};

// A user message's attachment is persisted as a marker at the end of its PROMPT row, so a reloaded
// chat still shows which file went with it. Mirrors the agent's artifact marker. `>` is escaped so a
// filename can't close the comment early, and U+2028/U+2029 because the regex's `.` won't match them.
const ATTACHMENT_MARKER_RE = /\n?<!--attachment:(\{.*\})-->$/;

const isChatAttachment = (value: unknown): value is ChatAttachment =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as ChatAttachment).filename === 'string' &&
  typeof (value as ChatAttachment).size === 'number';

export const withAttachmentMarker = (prompt: string, attachment?: ChatAttachment): string => {
  if (!attachment) return prompt;
  const json = JSON.stringify({ filename: attachment.filename, size: attachment.size })
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
  return [prompt, `<!--attachment:${json}-->`].filter(Boolean).join('\n');
};

export const splitAttachmentMarker = (
  contents: string
): { content: string; attachment?: ChatAttachment } => {
  const match = ATTACHMENT_MARKER_RE.exec(contents);
  if (!match) return { content: contents };
  try {
    const parsed: unknown = JSON.parse(match[1]);
    if (isChatAttachment(parsed)) {
      return { content: contents.slice(0, match.index), attachment: parsed };
    }
  } catch {
    // not a marker we wrote — leave the text as it is
  }
  return { content: contents };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kilobytes = Math.round(bytes / 1024);
  if (kilobytes < 1024) return `${kilobytes} KB`;
  return `${Number((bytes / (1024 * 1024)).toFixed(1))} MB`;
};

// Browsers report a CSV's type inconsistently (Excel on Windows says application/vnd.ms-excel, some
// report nothing), so the extension decides.
export const getCsvAttachmentError = (file: { name: string; size: number }): string | null => {
  if (!file.name.toLowerCase().endsWith('.csv')) return 'Only CSV files can be attached.';
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `Files must be ${formatFileSize(MAX_ATTACHMENT_BYTES)} or smaller.`;
  }
  return null;
};

// Read when picked, not when sent: a file re-saved or moved in between would otherwise fail after
// the message is already shown. FileReader is async, so a large file doesn't freeze the tab.
export const readCsvAttachment = (file: File): Promise<PendingAttachment> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file read result'));
        return;
      }
      resolve({
        filename: file.name,
        size: file.size,
        // The extension was checked on pick (getCsvAttachmentError), so the type is sent as
        // text/csv whatever the browser reported.
        mimeType: 'text/csv',
        // A data URL is `data:<type>;base64,<data>`.
        data: result.slice(result.indexOf(',') + 1)
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });

// Reconstruct the in-memory chat transcript from persisted llm_messages rows so a
// resumed session (same session id after a page reload) shows its prior turns
// instead of an empty thread. Artifact segments come back without a url — pre-signed
// S3 links are stripped before storage and render as an expired indicator.
export const hydrateStoredMessages = (stored: LLMMessage[]): AgentMessage[] =>
  stored.map(message =>
    message.type === 'PROMPT'
      ? {
          id: message.messageId,
          role: MessageRole.USER,
          ...splitAttachmentMarker(message.contents),
          timestamp: message.timestamp
        }
      : {
          id: message.messageId,
          role: MessageRole.ASSISTANT,
          segments: parseStoredSegments(message.contents),
          timestamp: message.timestamp,
          agent: message.agent,
          // The hydrated `id` above already IS the real llm_messages.id (unlike a
          // live-streamed message, whose client-generated id only gets a persistedId
          // once its save resolves) — feedback submission needs it set right away.
          persistedId: message.messageId,
          persistedFeedback: message.feedback,
          persistedFeedbackReason: message.feedbackReason
        }
  );

// The emit_download tool result reaches the client before the agent finishes writing
// its summary, so the raw stream order renders the download button above the answer
// (often mid-sentence, between an "I'll run it now." preamble and the results). Move
// artifact segments to the end so the download sits at the bottom of the message.
// This matches the chat export, which already appends artifacts after the text.
// Relative order within each group is preserved, so multiple downloads stay in the
// order the agent emitted them.
export const artifactSegmentsLast = (segments: AgentResponseSegment[]): AgentResponseSegment[] => [
  ...segments.filter(segment => segment.type !== 'artifact'),
  ...segments.filter(segment => segment.type === 'artifact')
];

// Agents write "~$1.2M" for "approximately"; remark-gfm's default singleTilde pairs two such
// tildes into strikethrough. Only `~~double~~` should strike through.
export const AGENT_CHAT_REMARK_PLUGINS: Options['remarkPlugins'] = [
  [remarkGfm, { singleTilde: false }]
];

// Tool names arrive as identifiers, optionally namespaced by MCP server ('server___tool_name').
// Drop the namespace and render the rest as a sentence: 'assemble_ask_oracle' -> 'Assemble ask oracle'.
export const humanizeToolName = (name: string): string =>
  humanize(name.replace(/^.*___/, '').replace(/-/g, '_'));

// Only absolute web links leave the app. Relative paths and #anchors point inside the current page
// and should keep navigating in place.
export const isExternalHref = (href: string | undefined): href is string =>
  href !== undefined && /^https?:\/\//i.test(href);

// Tool args often carry strings that are themselves JSON, which plain JSON.stringify renders as one
// long line full of \" escapes. Parse those into real nested values first so the output is still
// valid, copy-pasteable JSON but reads as structure. (Multi-line strings keep their \n escapes --
// JSON has no other way to represent them.)
const parseEmbeddedJson = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(parseEmbeddedJson);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, parseEmbeddedJson(v)]));
  }
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return parseEmbeddedJson(JSON.parse(trimmed));
  } catch {
    return value;
  }
};

export const formatToolArgs = (args: Record<string, unknown>): string =>
  JSON.stringify(parseEmbeddedJson(args), null, 2);

export interface ToolCallGroupSegment {
  type: 'tool_group';
  calls: AgentToolCallSegment[];
}

export type RenderedSegment = AgentResponseSegment | ToolCallGroupSegment;

// Calls group by what the chip displays: the title when present, else the tool name.
export const toolCallLabelKey = (call: AgentToolCallSegment): string => call.title ?? call.name;

// Collapses each run of consecutive calls with the same label into one group so a chatty agent
// doesn't fill the bubble with identical chips. Single calls pass through unchanged.
export const groupToolCalls = (segments: AgentResponseSegment[]): RenderedSegment[] => {
  const grouped: RenderedSegment[] = [];
  segments.forEach(segment => {
    const previous = grouped[grouped.length - 1];
    if (segment.type !== 'tool_call' || !previous) {
      grouped.push(segment);
      return;
    }
    if (previous.type === 'tool_call' && toolCallLabelKey(previous) === toolCallLabelKey(segment)) {
      grouped[grouped.length - 1] = { type: 'tool_group', calls: [previous, segment] };
    } else if (
      previous.type === 'tool_group' &&
      toolCallLabelKey(previous.calls[0]) === toolCallLabelKey(segment)
    ) {
      previous.calls.push(segment);
    } else {
      grouped.push(segment);
    }
  });
  return grouped;
};

// What a finished run means for its turn. A stopped run keeps (and persists) its partial reply but
// is not a finished answer, so it triggers no host callback or handoff; one stopped before it
// started has nothing to show or save.
export type TurnOutcome = 'complete' | 'stopped' | 'discarded';

export const getTurnOutcome = ({ cancelled, segments }: AgentResponse): TurnOutcome => {
  if (!cancelled) return 'complete';
  return segments.length > 0 ? 'stopped' : 'discarded';
};
